import type { Glyph, Pt } from './glyphs'
import type { Vec3 } from '@/play/ghost/strokePath'

/**
 * Glyphs in the world: a glyph laid on a plane of the world, and world points back into the glyph's
 * unit box (for checking a tracing). The ghost kit (src/play/ghost) draws the laid strokes.
 */

/**
 * A plane to lay glyphs on: glyph point (gx, gy) is at `origin + gx * u + gy * v` (world units, cell
 * centres at integers). On the floor seen from the front, u is +x and v is -z (away from the viewer).
 */
export type GlyphPlane = { origin: Vec3; u: Vec3; v: Vec3 }

/** A floor plane at height y for a glyph `height` cells tall, centred on (cx, cz), upright from the front. */
export function floorPlane(glyph: Glyph, cx: number, y: number, cz: number, height: number): GlyphPlane {
  return {
    origin: { x: cx - (glyph.width * height) / 2, y, z: cz + height / 2 },
    u: { x: height, y: 0, z: 0 },
    v: { x: 0, y: 0, z: -height },
  }
}

export function onPlane(plane: GlyphPlane, p: Pt): Vec3 {
  const { origin: o, u, v } = plane
  return { x: o.x + u.x * p[0] + v.x * p[1], y: o.y + u.y * p[0] + v.y * p[1], z: o.z + u.z * p[0] + v.z * p[1] }
}

/** A world point back into glyph units (projected onto the plane). */
export function fromPlane(plane: GlyphPlane, w: Vec3): Pt {
  const d = { x: w.x - plane.origin.x, y: w.y - plane.origin.y, z: w.z - plane.origin.z }
  const uu = dot(plane.u, plane.u), vv = dot(plane.v, plane.v)
  return [dot(d, plane.u) / uu, dot(d, plane.v) / vv]
}

/** A glyph's strokes in the world, in writing order. */
export function layGlyph(glyph: Glyph, plane: GlyphPlane): Vec3[][] {
  return glyph.strokes.map((s) => s.map((p) => onPlane(plane, p)))
}

function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z
}
